import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { Typography, Button } from "@mui/joy";
import { ResetWrenchRounded, AutoplayFilledRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { HeroListCard, HeroListRow } from "../dashboardHero/heroPrimitives";
import { SkeletonListRows, fadeInSx } from "../dashboardHero/HeroSkeletons";
import { ServiceReminderLike } from "../ServiceReminderCard";
import { ServiceReminderList } from "../../graphql/queries/serviceReminderList";
import { getServiceReminderStatus, isServiceReminderActionable, sortByNextDueDate } from "../../utils/serviceReminderStatus";

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    compact?: boolean;
}

// Due-date tint by urgency.
const DUE_COLOR: Record<string, string> = {
    overdue: "#d32f2f",
    dueSoon: "#ed6c02",
    ok: "#777",
    none: "#777",
};

/** Service reminders as a list card: task name + next-due date (tinted by urgency). */
const AssetServiceReminderWidget = ({ assetLocationId, installedBaseNodeId, compact }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const limit = compact ? 2 : 4;

    const { data, loading } = useQuery(ServiceReminderList, {
        variables: { installedBaseNodeId },
        skip: !installedBaseNodeId,
        fetchPolicy: "cache-and-network",
    });

    const reminders: ServiceReminderLike[] = _.get(data, "serviceReminderList.serviceReminders", []) ?? [];
    const total = _.get(data, "serviceReminderList.total", reminders.length) ?? 0;

    const sorted = useMemo(() => sortByNextDueDate(reminders).slice(0, limit), [reminders, limit]);
    const goToList = () => navigate(`/assets/${assetLocationId}/serviceReminders`);
    const goToReset = (reminderId: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/assets/${assetLocationId}/serviceReminders?reset=${reminderId}`);
    };

    return (
        <HeroListCard
            title={t("Service Reminders")}
            icon={<ResetWrenchRounded />}
            count={total}
            dense={compact}
            viewAll={t("View all reminders")}
            onViewAll={goToList}
        >
            {loading && !data ? (
                <SkeletonListRows rows={compact ? 2 : 3} />
            ) : total === 0 ? (
                <Typography level="bodySmall" sx={{ color: "#777" }}>
                    {t("No reminders")}
                </Typography>
            ) : (
                <Box sx={fadeInSx}>
                    {sorted.map((reminder) => {
                        const status = getServiceReminderStatus(reminder.nextDueDate);
                        const name = reminder.defaultServiceTask?.name || reminder.serviceTaskTypeKey;
                        const due = reminder.nextDueDate
                            ? moment(reminder.nextDueDate).format("DD.MM.YYYY")
                            : t("No due date");
                        return (
                            <HeroListRow
                                key={reminder.id}
                                icon={<ResetWrenchRounded />}
                                title={name}
                                meta={t("Due next")}
                                trailing={<span style={{ color: DUE_COLOR[status] }}>{due}</span>}
                                action={
                                    isServiceReminderActionable(status) ? (
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            startDecorator={<AutoplayFilledRounded />}
                                            onClick={goToReset(reminder.id)}
                                            sx={{ color: "#333", backgroundColor: "#FDDDCD", "&:hover": { backgroundColor: "#FFBA99" } }}
                                        >
                                            {t("Renew")}
                                        </Button>
                                    ) : undefined
                                }
                                onClick={goToList}
                            />
                        );
                    })}
                </Box>
            )}
        </HeroListCard>
    );
};

export default AssetServiceReminderWidget;
